/**
 * Draggable Course Box (dnd-kit Migration)
 * Course box that can be dragged AND dropped onto to swap sections
 */
import { memo } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import clsx from 'clsx';
import { getCourseColor } from '../utils/courseColor';

const DraggableCourseBox = memo(({
    code,
    section,
    room,
    timeStart,
    timeEnd,
    dates,
    instructor,
    isSingleton = false,
    isVirtual = false,
    hasConflict = false,
    occurrenceId,
    readOnly = false,
    reason,
}) => {
    const id = `${occurrenceId}-${isVirtual ? 'virtual' : 'real'}`;
    const droppableId = `drop-${id}`;

    // Extract section type (L, T, LA) for matching
    const sectionType = section.match(/^[A-Z]+/i)?.[0] || '';

    // Make this box draggable
    const {
        attributes,
        listeners,
        setNodeRef: setDragRef,
        isDragging
    } = useDraggable({
        id,
        disabled: isSingleton || readOnly || isVirtual,
        data: {
            code,
            section,
            sectionType,
            room,
            timeStart,
            timeEnd,
            isVirtual,
            isSingleton,
            occurrenceId,
        }
    });

    // Only virtual alternatives are useful drop targets. Registering real
    // occurrences lets them steal collision detection from replacement boxes.
    const { isOver, setNodeRef: setDropRef } = useDroppable({
        id: droppableId,
        disabled: isSingleton || readOnly || !isVirtual,
        data: {
            code,
            section,
            sectionType,
            isVirtual,
            occurrenceId,
        }
    });

    // Combine refs for both draggable and droppable
    const setNodeRef = (node) => {
        setDragRef(node);
        setDropRef(node);
    };

    // Use global color system
    const colorClass = `color${getCourseColor(code)}`;

    const roomShort = (room || 'TBA')
        .replace(/, Lift [0-9]+((-|,)( )*[0-9]+)?/gi, '')
        .replace(/\([0-9]+\)/gi, '')
        .replace(/Lecture Theater /gi, 'LT')
        .replace(/, [A-Z ]+/gi, '');

    // Build tooltip
    const tooltipLines = [];
    if (dates?.start && dates?.end) {
        tooltipLines.push(`${dates.start} - ${dates.end}`);
    }
    if (timeStart && timeEnd) {
        tooltipLines.push(`${timeStart} - ${timeEnd}`);
    }
    if (reason) {
        tooltipLines.push(reason);
    }
    tooltipLines.push(room || 'TBA');
    if (instructor && instructor.length > 0) {
        tooltipLines.push(`Instructor: ${instructor.join(', ')}`);
    }

    // Style with drag transform and drop highlight
    const style = {
        // Transformations are handled by DragOverlay, so we don't move the source box
        // We only change opacity to show it's being dragged
        opacity: isDragging ? 0.3 : 1, // Make it more transparent to clearly show it's a "ghost" of the original
        // Highlight when another box is being dropped on this one
        outline: isOver ? '3px dashed #2563eb' : undefined,
        outlineOffset: isOver ? '2px' : undefined,
    };

    // Build tooltip content for Tippy
    const tooltipContent = isVirtual ? null : (
        <div style={{ whiteSpace: 'pre-line' }}>
            {tooltipLines.join('\n')}
        </div>
    );

    return (
        <Tippy
            content={tooltipContent}
            placement="right"
            offset={[5, 15]}
            disabled={isVirtual}
        >
            <div
                ref={setNodeRef}
                style={style}
                name={`${code}_${section}`}
                className={clsx(
                    'lesson',
                    colorClass,
                    {
                        'draggable': !isSingleton && !readOnly && !isVirtual,
                        virtual: isVirtual,
                        'time-conflict': hasConflict,
                        'drop-target-active': isOver,
                    },
                    code,
                    section
                )}
                {...attributes}
                {...listeners}
            >
                <div>
                    {code}<br />
                    {section}<br />
                    {roomShort}
                </div>
            </div>
        </Tippy>
    );
});

DraggableCourseBox.displayName = 'DraggableCourseBox';

export default DraggableCourseBox;
